import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, User, Trophy, Star, Pencil, NirCoinIcon, X, Camera } from "../components/common/Icons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ user, nirtcgBalance, isLoggedIn, onOpenAuthModal, onUpdateUser }) => {
  const [userRanking, setUserRanking] = useState(null);
  const [featuredCards, setFeaturedCards] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [showTournaments, setShowTournaments] = useState(false);
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [newPictureUrl, setNewPictureUrl] = useState('');
  const [savingPicture, setSavingPicture] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    if (isLoggedIn && user?.user_id) {
      loadUserData();
      loadTournaments();
    }
  }, [isLoggedIn, user?.user_id]);

  const loadUserData = async () => {
    try {
      const [rankingRes, userRes] = await Promise.all([
        axios.get(`${API}/users/${user.user_id}/ranking`).catch(() => ({ data: null })),
        axios.get(`${API}/users/${user.user_id}`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      setUserRanking(rankingRes.data);
      setFeaturedCards(userRes.data.featured_cards || []);
    } catch (e) {
      console.error('Error loading user data:', e);
    }
  };

  const loadTournaments = async () => {
    try {
      const res = await axios.get(`${API}/tournaments`);
      setTournaments(res.data.tournaments || []);
    } catch (e) {
      console.error('Error loading tournaments:', e);
    }
  };

  const handleRegisterTournament = async (tournamentId, useNirCoin) => {
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/register?use_nircoin=${useNirCoin}`, {}, { withCredentials: true });
      alert('¡Te has inscrito exitosamente!');
      loadTournaments();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al inscribirse');
    }
  };

  const handleUpdatePicture = async () => {
    if (!newPictureUrl.trim()) return;
    setSavingPicture(true);
    try {
      await axios.post(`${API}/users/update-picture`, { picture_url: newPictureUrl }, { withCredentials: true });
      if (onUpdateUser) onUpdateUser({ ...user, picture: newPictureUrl });
      setShowPictureModal(false);
      setNewPictureUrl('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al actualizar la foto');
    }
    setSavingPicture(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/profile-photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data.url;
      if (onUpdateUser) onUpdateUser({ ...user, picture: url });
      setShowPictureModal(false);
      setNewPictureUrl('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al subir la foto');
    }
    setUploadingFile(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center" data-testid="profile-page">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">Acceso Requerido</h1>
          <p className="text-gray-400 mb-6 text-sm md:text-base">Debes iniciar sesión para ver tu perfil</p>
          <button 
            onClick={onOpenAuthModal}
            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-red-500 rounded-full text-white font-semibold"
          >
            Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="profile-page">
      <div className="container mx-auto px-4 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors text-sm">
          <ArrowLeft /> Volver al Inicio
        </Link>
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl">
        {/* Profile Header */}
        <div className="glass-card rounded-xl md:rounded-2xl p-6 md:p-8 mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <div className="relative group">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden ring-4 ring-purple-500/30">
                {user?.picture ? (
                  <img src={user.picture} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-black text-3xl">{user?.name?.charAt(0) || '?'}</span>
                )}
              </div>
              {/* Edit Photo Button */}
              <button
                onClick={() => { setShowPictureModal(true); setNewPictureUrl(user?.picture || ''); }}
                className="absolute bottom-0 right-0 w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-pink-500/50 transition-all opacity-80 group-hover:opacity-100"
                title="Cambiar foto"
                data-testid="change-photo-btn"
              >
                <Camera size={16} className="text-white" />
              </button>
            </div>
            <div className="text-center md:text-left flex-1">
              <h1 className="text-2xl md:text-3xl font-black text-white mb-1">{user?.name}</h1>
              <p className="text-gray-400 text-sm md:text-base mb-3">{user?.email}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-3 md:gap-4">
                <Link to="/wallet" className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-900/50 to-yellow-900/50 border border-yellow-600/50 rounded-full hover:border-yellow-400 transition-colors">
                  <NirCoinIcon size={18} />
                  <span className="font-bold text-yellow-400 text-sm">{nirtcgBalance?.toFixed(2)} NirCoins</span>
                </Link>
                {userRanking?.position && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-900/50 border border-purple-600/50 rounded-full">
                    <Trophy size={18} className="text-yellow-400" />
                    <span className="font-bold text-purple-300 text-sm">#{userRanking.position}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="glass-card rounded-lg md:rounded-xl p-4 text-center">
            <Trophy size={24} className="mx-auto text-yellow-400 mb-2" />
            <p className="text-xl md:text-2xl font-black text-yellow-400">{userRanking?.points || 0}</p>
            <p className="text-gray-400 text-xs md:text-sm">Puntos</p>
          </div>
          <div className="glass-card rounded-lg md:rounded-xl p-4 text-center">
            <Star filled className="mx-auto text-pink-400 mb-2" />
            <p className="text-xl md:text-2xl font-black text-pink-400">0</p>
            <p className="text-gray-400 text-xs md:text-sm">Torneos</p>
          </div>
          <div className="glass-card rounded-lg md:rounded-xl p-4 text-center">
            <Trophy size={24} className="mx-auto text-green-400 mb-2" />
            <p className="text-xl md:text-2xl font-black text-green-400">0</p>
            <p className="text-gray-400 text-xs md:text-sm">Victorias</p>
          </div>
          <div className="glass-card rounded-lg md:rounded-xl p-4 text-center">
            <NirCoinIcon size={24} />
            <p className="text-xl md:text-2xl font-black text-amber-400 mt-2">{Math.floor(nirtcgBalance || 0)}</p>
            <p className="text-gray-400 text-xs md:text-sm">NirCoins</p>
          </div>
        </div>

        {/* Upcoming Tournaments */}
        <div className="glass-card rounded-xl md:rounded-2xl p-4 md:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-yellow-400">Próximos Torneos</h2>
            <button 
              onClick={() => setShowTournaments(!showTournaments)}
              className="text-sm text-pink-400 hover:text-pink-300"
            >
              {showTournaments ? 'Ocultar' : 'Ver todos'}
            </button>
          </div>
          
          {showTournaments && (
            <div className="space-y-3">
              {tournaments.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No hay torneos disponibles</p>
              ) : (
                tournaments.slice(0, 3).map(t => (
                  <div key={t.id} className="p-4 bg-purple-900/30 rounded-xl">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-white">{t.name}</h3>
                        <p className="text-sm text-gray-400">{t.location}</p>
                        <p className="text-xs text-pink-400 mt-1">
                          {new Date(t.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">{t.current_participants}/{t.max_participants || '∞'} inscritos</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button 
                        onClick={() => handleRegisterTournament(t.id, false)}
                        className="flex-1 py-2 bg-pink-500/20 text-pink-400 rounded-lg text-sm hover:bg-pink-500/30"
                      >
                        Inscribirse (${t.entry_fee_usd})
                      </button>
                      {t.entry_fee_nircoin && (
                        <button 
                          onClick={() => handleRegisterTournament(t.id, true)}
                          className="flex-1 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30 flex items-center justify-center gap-1"
                        >
                          <NirCoinIcon size={14} />
                          {t.entry_fee_nircoin} NC
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {!showTournaments && (
            <p className="text-gray-500 text-sm text-center">Haz clic en "Ver todos" para inscribirte en torneos usando NirCoins</p>
          )}
        </div>

        {/* Featured Cards Section */}
        <div className="glass-card rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-yellow-400">Cartas Destacadas</h2>
            <button 
              onClick={() => setIsEditing(!isEditing)}
              className="px-3 py-1.5 text-sm bg-purple-800/50 text-purple-300 rounded-lg hover:bg-purple-700/50 transition-colors"
            >
              {isEditing ? 'Guardar' : 'Editar'}
            </button>
          </div>
          <p className="text-gray-400 text-xs md:text-sm mb-4">Muestra hasta 10 cartas favoritas en tu perfil</p>
          
          {featuredCards.length > 0 ? (
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
              {featuredCards.map((card, index) => (
                <div key={index} className="aspect-[3/4] bg-purple-900/50 rounded-lg overflow-hidden relative group">
                  <img src={card.image_url} alt={card.name} className="w-full h-full object-cover" />
                  {isEditing && (
                    <button 
                      onClick={() => setFeaturedCards(featuredCards.filter((_, i) => i !== index))}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 md:py-12 border-2 border-dashed border-purple-700 rounded-xl">
              <p className="text-gray-500 text-sm md:text-base">No has seleccionado cartas destacadas</p>
              <button className="mt-3 px-4 py-2 bg-purple-800/50 text-purple-300 rounded-lg hover:bg-purple-700/50 transition-colors text-sm">
                Añadir Cartas
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Change Picture Modal */}
      {showPictureModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(8px)' }}>
          <div className="glass-card rounded-xl p-6 max-w-md w-full" style={{ border: '1px solid rgba(168,85,247,0.3)' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-yellow-400">Cambiar Foto de Perfil</h2>
              <button onClick={() => { setShowPictureModal(false); setNewPictureUrl(''); }} className="text-gray-400 hover:text-white transition-colors">
                <X />
              </button>
            </div>
            
            {/* Preview */}
            <div className="flex justify-center mb-5">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center overflow-hidden ring-4 ring-purple-500/30">
                {(newPictureUrl || user?.picture) ? (
                  <img src={newPictureUrl || user.picture} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                ) : (
                  <span className="text-white font-black text-2xl">{user?.name?.charAt(0) || '?'}</span>
                )}
              </div>
            </div>

            {/* Upload from Device - PRIMARY */}
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)' }}>
              <p className="text-sm font-semibold text-purple-300 mb-3 flex items-center gap-2">
                <Camera size={16} />
                Subir desde tu dispositivo
              </p>
              <label className="block cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                  data-testid="profile-file-input"
                />
                <div className="flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', boxShadow: '0 4px 15px rgba(168,85,247,0.3)' }}>
                  {uploadingFile ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span className="text-white">Subiendo...</span></>
                  ) : (
                    <><Camera size={18} className="text-white" /><span className="text-white">Elegir foto (JPG, PNG, WebP)</span></>
                  )}
                </div>
              </label>
              <p className="text-xs text-gray-500 mt-2 text-center">Compatible con fotos de celular y computadora</p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-700" />
              <span className="text-gray-500 text-xs">o ingresa una URL</span>
              <div className="flex-1 h-px bg-gray-700" />
            </div>
            
            <div className="mb-4">
              <input 
                type="text" 
                placeholder="https://ejemplo.com/mi-foto.jpg" 
                value={newPictureUrl}
                onChange={(e) => setNewPictureUrl(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-800 border border-purple-700/50 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-pink-500 transition-colors"
                data-testid="picture-url-input"
              />
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => { setShowPictureModal(false); setNewPictureUrl(''); }} 
                className="flex-1 py-2.5 border border-gray-600 rounded-lg text-gray-400 hover:bg-gray-800 text-sm transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleUpdatePicture}
                disabled={savingPicture || !newPictureUrl.trim()}
                className="flex-1 py-2.5 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg font-semibold text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
                data-testid="save-picture-btn"
              >
                {savingPicture ? 'Guardando...' : 'Guardar URL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
